// 2개종목비교 그래프 공용 로직/컴포넌트
// - 시장지수현황(MarketIndexPage)의 '2개종목비교' 탭
// - 건별수익률현황(ProfitStatusPage) 종목별 탭의 차수 클릭 팝업(StockCompareModal)
// 양쪽에서 동일한 정규화·차트 렌더링 로직을 재사용하기 위해 분리함 (2026-09)
import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import './CompareChart.css';

// 색상: 국내ETF=blue, 비교대상(해외종목/지수)=aqua, 환율(원달러)=yellow(연한 노랑/주황) 고정
// (dataviz 스킬 categorical 팔레트 slot1/3/4, 3색 조합 검증 완료 — 인접쌍 CVD ΔE 9.1 이상)
export const COMPARE_COLORS = {
  domestic: '#2a78d6',
  compare:  '#1baf7a',
  fx:       '#eda100',
};

// 시작 시점(첫 유효값)을 100으로 정규화. rows: [{price_date, current_price}] price_date ASC 가정
export const normalizeSeries = (rows) => {
  const valid = (rows || []).filter(r => r.current_price != null);
  if (!valid.length) return [];
  const base = valid[0].current_price;
  if (!base) return [];
  return valid.map(r => ({ date: r.price_date, value: r.current_price / base * 100 }));
};

// tb_market_index(DESC/ASC 무관) 중 특정 필드를 {price_date, current_price} ASC 배열로 변환
export const marketFieldToSeries = (marketData, field) => [...(marketData || [])]
  .filter(d => d[field] != null)
  .map(d => ({ price_date: d.index_date, current_price: d[field] }))
  .sort((a, b) => a.price_date.localeCompare(b.price_date));

// 정규화된 여러 시리즈를 날짜 기준으로 병합 (거래일이 서로 달라도 outer join)
export const mergeNormalized = (seriesMap) => {
  const byDate = {};
  Object.entries(seriesMap).forEach(([key, arr]) => {
    arr.forEach(p => {
      if (!byDate[p.date]) byDate[p.date] = { date: p.date };
      byDate[p.date][key] = p.value;
    });
  });
  return Object.keys(byDate).sort().map(d => byDate[d]);
};

// 조회기간 종료시점 기준 총 변화율(%) — 정규화 시리즈의 마지막 유효값 - 100
export const totalChangeRate = (normalized) => {
  const valid = (normalized || []).filter(p => p.value != null);
  if (!valid.length) return null;
  return Math.round((valid[valid.length - 1].value - 100) * 10) / 10;
};

export const CompareTooltip = ({ active, payload, label, series }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="cmp-tooltip">
      <div className="cmp-tt-date">{label}</div>
      {series.map(s => d[s.key] != null && (
        <div key={s.key} style={{ color: s.color, fontWeight: 600, fontSize: 12, marginTop: 2 }}>
          {s.label}: {Number(d[s.key]).toFixed(1)}
        </div>
      ))}
    </div>
  );
};

// 보조축(우측) 도메인 계산 — 100을 중심으로 실제 값 범위에 딱 맞춘 대칭 범위를 만들어,
// 주축(좌측)과 별개 스케일이면서도 정규화 시작점(=100)이 차트 세로 중앙에 오도록 함
// (환율처럼 변동폭이 작은 시리즈가 축 전체를 다 써서 작은 등락도 잘 보이게 하기 위함)
const rightAxisDomain = (mergedData, rightKeys) => {
  const values = mergedData.flatMap(d => rightKeys.map(k => d[k]).filter(v => v != null));
  if (!values.length) return ['auto', 'auto'];
  const maxAbsDev = Math.max(...values.map(v => Math.abs(v - 100)));
  const pad = Math.max(2, Math.ceil(maxAbsDev * 1.8));
  return [100 - pad, 100 + pad];
};

// 정규화 비교 차트 (2~3라인).
// series: [{key,label,color,axis}] — axis:'right'인 항목은 보조축(secondary y-axis)에 그려짐
// (환율처럼 변동폭이 작아 같은 축에 있으면 평탄하게 보이는 시리즈에 사용)
export const CompareLineChart = ({ title, desc, mergedData, series, height = 260 }) => {
  const rightKeys = series.filter(s => s.axis === 'right').map(s => s.key);
  const hasRight = rightKeys.length > 0;
  const rightDomain = hasRight ? rightAxisDomain(mergedData, rightKeys) : undefined;
  return (
    <div className="cmp-chart-panel">
      <div className="cmp-chart-header cmp-chart-header--dual">
        <div className="cmp-dual-left">
          <div className="cmp-chart-title">{title}</div>
          <div className="cmp-chart-desc">{desc || '조회기간 시작 시점 = 100 기준 정규화'}</div>
        </div>
        <div className="cmp-dual-legend">
          {series.map(s => (
            <span key={s.key} style={{ color: s.color }}>
              ─ {s.label}{s.axis === 'right' ? ' (보조축)' : ''}
            </span>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={mergedData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#888' }} tickFormatter={d => d.slice(5)} minTickGap={30} />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 10, fill: '#888' }}
            tickFormatter={v => Number(v).toFixed(0)}
            width={48}
            domain={['auto', 'auto']}
          />
          {hasRight && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 10, fill: '#888' }}
              tickFormatter={v => Number(v).toFixed(0)}
              width={48}
              domain={rightDomain}
            />
          )}
          <Tooltip content={<CompareTooltip series={series} />} />
          <ReferenceLine yAxisId="left" y={100} stroke="#ccc" strokeDasharray="2 2" />
          {series.map(s => (
            <Line
              key={s.key}
              yAxisId={s.axis === 'right' ? 'right' : 'left'}
              type="monotone"
              dataKey={s.key}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
